import type { MapEngine } from '@mighty-map/map-engine';
import { create } from 'zustand';
import type { GeometryType } from '../../api/types';
import type { ImportSub } from '../../lib/data-passport/imports';
import { hiddenTargets, legendPaintOf, type RuntimeLayer, type RuntimeLegendRow } from '../../lib/layers-pro/runtime';
import { layerIdsUnder } from '../../lib/layers-pro/rows';
import { towerPaint } from '../../lib/widget-host/hud/paint';
import { selectActiveTheme, useMapStore } from '../../state/mapStore';
import { useAddedLayers } from '../data-passport/addedLayers';
import { usePassport } from '../data-passport/store';

/**
 * The layers tools add to the map while the app runs, listed in Layers Pro the way production's layer list showed
 * them (lib/layers-pro/runtime): Data Passport's layers from other themes and its imports, and the Coverage HUD's
 * layer. Layers Pro switches them on and off like any other: a published layer through the map view, an engine
 * overlay through `runtimeAware`, which every tool's engine passes through, so a switched-off overlay stays off however
 * often its tool redraws it.
 *
 * Loaded with the shell, so it sees every add in the order it happens (the list's order).
 */

/** A runtime layer as a tool describes it; the registry stamps when it joined the map. */
export type RuntimeLayerSpec = Omit<RuntimeLayer, 'seq'>;

interface RuntimeState {
  /** By source, so a tool replaces its own set without touching another's. */
  bySource: Record<string, readonly RuntimeLayer[]>;
  /** Engine overlays on the map now, each with when it was first drawn: a described overlay is listed only while
   *  it is drawn, and in the place it took on the map. */
  drawn: Record<string, number>;
  /** Switched off in Layers Pro: runtime layer ids and group keys. */
  hidden: Record<string, boolean>;
}

let seq = 0;

export const useRuntimeLayers = create<RuntimeState>()(() => ({ bySource: {}, drawn: {}, hidden: {} }));

/** Replace a source's runtime layers. A layer keeps its place in the list for as long as it stays on the map. */
export function publishRuntimeLayers(source: string, specs: readonly RuntimeLayerSpec[]): void {
  const before = new Map((useRuntimeLayers.getState().bySource[source] ?? []).map((l) => [l.id, l]));
  const next = specs.map((s): RuntimeLayer => ({ ...s, seq: before.get(s.id)?.seq ?? ++seq }));
  useRuntimeLayers.setState((st) => ({ bySource: { ...st.bySource, [source]: next } }));
}

export function setRuntimeVisible(key: string, on: boolean): void {
  useRuntimeLayers.setState((st) => {
    const hidden = { ...st.hidden };
    if (on) delete hidden[key];
    else hidden[key] = true;
    return { hidden };
  });
}

/** Every runtime layer on the map now, from every source; an overlay's layers in the place the overlay took. */
export function runtimeLayersOf(st: Pick<RuntimeState, 'bySource' | 'drawn'>): RuntimeLayer[] {
  return Object.values(st.bySource)
    .flat()
    .flatMap((l): RuntimeLayer[] => {
      if (l.target.kind === 'layer') return [l];
      const at = st.drawn[l.target.overlayId];
      return at === undefined ? [] : [{ ...l, seq: at }];
    });
}

/* ── Data Passport: layers added from other themes, and imports ───────────────────────────────────────────────── */

const geometryOf = (sub: ImportSub): GeometryType => sub.features[0]?.geometry.type ?? 'Point';

/** An import, one layer: production's KML group switched its folders separately; here they switch together. */
function importSpecs(): RuntimeLayerSpec[] {
  return usePassport.getState().imports.map((entry): RuntimeLayerSpec => ({
    id: `import:${entry.id}`,
    title: entry.title,
    source: 'Data Passport',
    groupPath: [],
    legend: entry.subs.flatMap((sub): RuntimeLegendRow[] => {
      const first = sub.features[0];
      const geometry = geometryOf(sub);
      return first ? [{ label: sub.title, geometry, paint: legendPaintOf(first.paint, geometry) }] : [];
    }),
    target: { kind: 'overlay', overlayId: `data-passport-import-${entry.id}` },
  }));
}

/** A layer from another theme: drawn on top of the theme's own, until the theme in front holds it itself. */
function addedSpecs(): RuntimeLayerSpec[] {
  const s = useMapStore.getState();
  const theme = selectActiveTheme(s);
  const inTheme = new Set(theme ? layerIdsUnder(theme.tree) : []);
  return useAddedLayers.getState().added.flatMap((a): RuntimeLayerSpec[] => {
    const cfg = s.layers.get(a.layerId);
    if (!cfg || inTheme.has(a.layerId)) return [];
    return [
      {
        id: `added:${a.layerId}`,
        title: a.title,
        source: 'Data Passport',
        groupPath: a.groupPath,
        legend: cfg.legend.map((e) => ({ label: e.label ?? cfg.title, geometry: cfg.geometry.type, paint: e.paint })),
        target: { kind: 'layer', layerId: a.layerId },
      },
    ];
  });
}

useAddedLayers.subscribe((s, prev) => {
  if (s.added !== prev.added) publishRuntimeLayers('data-passport-added', addedSpecs());
});
useMapStore.subscribe((s, prev) => {
  if (s.layers !== prev.layers || s.themeId !== prev.themeId || s.config !== prev.config)
    publishRuntimeLayers('data-passport-added', addedSpecs());
});
usePassport.subscribe((s, prev) => {
  if (s.imports !== prev.imports) publishRuntimeLayers('data-passport-import', importSpecs());
});

/* ── The Coverage HUD ────────────────────────────────────────────────────────────────────────────────────────────
 * Production's Tower Construction Progress demo added a client-side layer titled "Tower construction progress", its
 * legend the two symbols it draws. The HUD draws it as the engine overlay below; it is listed while it is drawn. The
 * live analyses' layers take their titles from the analysis, which only the HUD knows: they are listed once the HUD
 * publishes them itself (publishRuntimeLayers('hud', ...)). */
const HUD_TOWERS_OVERLAY = 'wh-hud-towers';
const towerRow = (label: string, done: boolean): RuntimeLegendRow => ({
  label,
  geometry: 'Point',
  paint: legendPaintOf(towerPaint(done), 'Point'),
});
publishRuntimeLayers('hud-towers', [
  {
    id: 'hud:towers',
    title: 'Tower construction progress',
    source: 'Coverage HUD',
    groupPath: [],
    legend: [towerRow('Constructed', true), towerRow('Scheduled', false)],
    target: { kind: 'overlay', overlayId: HUD_TOWERS_OVERLAY },
  },
]);

/* ── visibility on the engine ───────────────────────────────────────────────────────────────────────────────────── */

type SetOverlayArgs = Parameters<MapEngine['draw']['setOverlay']>;
type ImageOverlay = Parameters<NonNullable<MapEngine['setImage']>>[1];

const wrapped = new WeakMap<MapEngine, MapEngine>();

/** Forward every member of `target`, methods bound to it, except the overrides. */
function forward<T extends object>(target: T, overrides: Partial<T>): T {
  const bound = new Map<PropertyKey, unknown>();
  return new Proxy(target, {
    get(t, prop) {
      if (Object.prototype.hasOwnProperty.call(overrides, prop)) return overrides[prop as keyof T];
      const v: unknown = Reflect.get(t, prop, t);
      if (typeof v !== 'function') return v;
      let b = bound.get(prop);
      if (!b) {
        b = (v as (...args: unknown[]) => unknown).bind(t);
        bound.set(prop, b);
      }
      return b;
    },
  });
}

const hiddenOverlays = (st: RuntimeState): Set<string> => hiddenTargets(runtimeLayersOf(st), st.hidden).overlays;

function noteDrawn(id: string, on: boolean): void {
  const known = useRuntimeLayers.getState().drawn[id] !== undefined;
  if (known === on) return;
  useRuntimeLayers.setState((st) => {
    const drawn = { ...st.drawn };
    if (on) drawn[id] = ++seq;
    else delete drawn[id];
    return { drawn };
  });
}

/**
 * The engine as the tools see it: the same engine, except that an overlay or a picture (setImage) Layers Pro has
 * switched off is held back (and drawn again, as it was last given, when it is switched back on). One wrapper per
 * engine, so every tool shares it; everything else passes straight through.
 */
export function runtimeAware(engine: MapEngine): MapEngine {
  const known = wrapped.get(engine);
  if (known) return known;
  const raw = engine.draw;
  const given = new Map<string, SetOverlayArgs>();
  const pictures = new Map<string, ImageOverlay>();
  let off = hiddenOverlays(useRuntimeLayers.getState());
  const draw = forward(raw, {
    setOverlay: (...args: SetOverlayArgs) => {
      const [id, features] = args;
      given.set(id, args);
      if (off.has(id)) raw.clearOverlay(id);
      else raw.setOverlay(...args);
      noteDrawn(id, features.length > 0);
    },
    clearOverlay: (id: string) => {
      given.delete(id);
      raw.clearOverlay(id);
      noteDrawn(id, false);
    },
  });
  const rawSetImage = engine.setImage?.bind(engine);
  const setImage = rawSetImage
    ? (id: string, picture: ImageOverlay | null): void => {
        if (picture) pictures.set(id, picture);
        else pictures.delete(id);
        rawSetImage(id, picture && !off.has(id) ? picture : null);
        noteDrawn(id, picture !== null);
      }
    : undefined;
  useRuntimeLayers.subscribe((st) => {
    const now = hiddenOverlays(st);
    for (const [id, args] of given) {
      if (now.has(id) && !off.has(id)) raw.clearOverlay(id);
      else if (!now.has(id) && off.has(id)) raw.setOverlay(...args);
    }
    for (const [id, picture] of pictures) {
      if (now.has(id) && !off.has(id)) rawSetImage?.(id, null);
      else if (!now.has(id) && off.has(id)) rawSetImage?.(id, picture);
    }
    off = now;
  });
  const out = forward(engine, setImage ? { draw, setImage } : { draw });
  wrapped.set(engine, out);
  return out;
}
